import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Loader2,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
} from "lucide-react";
import { useCertificateHolders } from "@/lib/api";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { CertificateHolder } from "@shared/schema";
import { CertificateHolderFormDialog } from "@/components/companies/CertificateHolderFormDialog";
import { DeleteCertificateHolderDialog } from "@/components/companies/DeleteCertificateHolderDialog";

// CSR-745 T8 + UI redesign: paginated certificate-holders list. Mirrors
// `Insureds.tsx` (CSR-674) primitives -- URL-resident pagination/search/
// sort, debounced search, `placeholderData: keepPreviousData` for smooth
// page transitions. The redesign:
//   * Section header ("Certificate Holders" + "{N} records -- auto-tracked
//     from sent COIs") with an in-content "+ New Holder" button.
//   * Trimmed columns: Name | Address | Email | COIs | Last Sent | Actions.
//   * `COIs` shows a Badge with the usage count.
//   * `Last Sent` is formatted as "MMM d, yyyy" (e.g. "May 1, 2026").
//   * Empty cells render as a single hyphen, not an em dash.

type SortField = "name" | "usageCount" | "lastUsedAt" | "createdAt";
type SortDir = "asc" | "desc";

const VALID_SORTS: SortField[] = ["name", "usageCount", "lastUsedAt", "createdAt"];

function formatLastSent(value: Date | string | null | undefined): string {
  if (!value) return "-";
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d.getTime())) return "-";
  return format(d, "MMM d, yyyy");
}

function isProbablyEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function CertificateHolders() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const qParam = params.get("q") || "";
  const rawSort = params.get("sort");
  const sortColumn: SortField =
    rawSort && (VALID_SORTS as string[]).includes(rawSort)
      ? (rawSort as SortField)
      : "usageCount";
  const sortDirection: SortDir = params.get("sortDir") === "asc" ? "asc" : "desc";
  const pageSize = 25;

  const [qInput, setQInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(qInput, 250);

  const setSearchParam = (key: string, value: string | null, resetPage = false) => {
    const next = new URLSearchParams(search);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (resetPage) next.delete("page");
    const qs = next.toString();
    navigate(qs ? `/companies?${qs}` : "/companies", { replace: false });
  };

  useEffect(() => {
    if (debouncedQ === qParam) return;
    setSearchParam("q", debouncedQ || null, /*resetPage*/ true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  useEffect(() => {
    setQInput(qParam);
  }, [qParam]);

  const { data, isLoading, isPlaceholderData } = useCertificateHolders({
    page,
    pageSize,
    q: debouncedQ || undefined,
    sort: sortColumn,
    sortDir: sortDirection,
  });

  const holders = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const setPage = (next: number) =>
    setSearchParam("page", next > 1 ? String(next) : null);

  const handleSort = (column: SortField) => {
    let nextDir: SortDir;
    if (sortColumn === column) {
      nextDir = sortDirection === "asc" ? "desc" : "asc";
    } else {
      nextDir = "asc";
    }
    const next = new URLSearchParams(search);
    next.set("sort", column);
    next.set("sortDir", nextDir);
    next.delete("page");
    navigate(`/companies?${next.toString()}`, { replace: false });
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [editHolder, setEditHolder] = useState<CertificateHolder | null>(null);
  const [deleteHolder, setDeleteHolder] = useState<CertificateHolder | null>(null);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4 shrink-0">
        <div>
          <h2 className="text-xl font-extrabold font-heading text-foreground">
            Certificate Holders
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {total} {total === 1 ? "record" : "records"} &mdash; auto-tracked from sent COIs
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="button-new-certificate-holder"
          className="gap-1.5"
        >
          <Plus className="w-4 h-4" /> New Holder
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 shrink-0">
        <div className="relative w-full max-w-lg">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder="Search by name, email, or address..."
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            className="pl-9"
            data-testid="input-search-certificate-holders"
          />
        </div>
      </div>

      {isLoading && !isPlaceholderData ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <Card className="border-border shadow-none overflow-hidden flex-1 flex flex-col min-h-0">
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-sm text-left">
              <thead className="text-sm text-foreground bg-muted/50 border-b border-border sticky top-0 z-10">
                <tr>
                  <th
                    className="px-4 py-2.5 font-semibold cursor-pointer select-none hover:text-foreground/80 transition-colors text-left"
                    onClick={() => handleSort("name")}
                    data-testid="header-sort-name"
                  >
                    <span className="inline-flex items-center gap-1">
                      Name
                      {sortColumn === "name" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowDown className="w-3.5 h-3.5" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />
                      )}
                    </span>
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-left">Address</th>
                  <th className="px-4 py-2.5 font-semibold text-left">Email</th>
                  <th
                    className="px-4 py-2.5 font-semibold cursor-pointer select-none hover:text-foreground/80 transition-colors text-center"
                    onClick={() => handleSort("usageCount")}
                    data-testid="header-sort-usageCount"
                  >
                    <span className="inline-flex items-center gap-1">
                      COIs
                      {sortColumn === "usageCount" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowDown className="w-3.5 h-3.5" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />
                      )}
                    </span>
                  </th>
                  <th
                    className="px-4 py-2.5 font-semibold cursor-pointer select-none hover:text-foreground/80 transition-colors text-left"
                    onClick={() => handleSort("lastUsedAt")}
                    data-testid="header-sort-lastUsedAt"
                  >
                    <span className="inline-flex items-center gap-1">
                      Last Sent
                      {sortColumn === "lastUsedAt" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3.5 h-3.5" />
                        ) : (
                          <ArrowDown className="w-3.5 h-3.5" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />
                      )}
                    </span>
                  </th>
                  <th className="px-4 py-2.5 w-20 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {holders.map((holder) => (
                  <tr
                    key={holder.id}
                    className="transition-colors hover:bg-muted/30"
                    data-testid={`row-holder-${holder.id}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-md bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                          <ShieldCheck className="w-3.5 h-3.5" />
                        </div>
                        <p className="font-medium text-foreground truncate">{holder.name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground max-w-xs truncate" title={holder.address || ""}>
                      {holder.address ? (
                        holder.address
                      ) : (
                        <span className="italic text-muted-foreground/60">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground max-w-[220px] truncate" title={holder.contactEmail || ""}>
                      {holder.contactEmail ? (
                        isProbablyEmail(holder.contactEmail) ? (
                          <a
                            href={`mailto:${encodeURIComponent(holder.contactEmail)}`}
                            className="hover:underline"
                          >
                            {holder.contactEmail}
                          </a>
                        ) : (
                          <span>{holder.contactEmail}</span>
                        )
                      ) : (
                        <span className="italic text-muted-foreground/60">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge variant="outline" className="font-semibold">
                        {holder.usageCount ?? 0}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                      {formatLastSent(holder.lastUsedAt as unknown as string | null)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setEditHolder(holder)}
                          data-testid={`button-edit-holder-${holder.id}`}
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                          onClick={() => setDeleteHolder(holder)}
                          data-testid={`button-delete-holder-${holder.id}`}
                          title="Archive"
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {holders.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                      No certificate holders yet. Add one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t shrink-0">
              <p className="text-sm text-muted-foreground">
                {((page - 1) * pageSize + 1).toLocaleString()}&ndash;
                {Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1 || isPlaceholderData}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isPlaceholderData}
                >
                  Previous
                </Button>
                <span className="text-sm px-3">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isPlaceholderData}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages || isPlaceholderData}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <CertificateHolderFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
      />

      {editHolder && (
        <CertificateHolderFormDialog
          open={!!editHolder}
          onOpenChange={(open) => {
            if (!open) setEditHolder(null);
          }}
          mode="edit"
          holderId={editHolder.id}
        />
      )}

      {deleteHolder && (
        <DeleteCertificateHolderDialog
          open={!!deleteHolder}
          onOpenChange={(open) => {
            if (!open) setDeleteHolder(null);
          }}
          holder={deleteHolder}
        />
      )}
    </>
  );
}
